import { get, post, remove } from 'constants/api/ApiClient'
import { DefaultApiResponse } from 'constants/api/models/response/DefaultApiResponse'
import { logError } from 'services/logService/errorLogger'
import { PendingInvite } from 'pages/auth/AcceptInvitePage'

const fileName = 'TeamApiService'

export interface OrgMember {
    id: string
    invitedUserId: string
    firstName: string
    lastName: string
    email: string
    status: 'pending' | 'accepted'
    accountStatus: string
    joinedAt: string
}

export interface SeatSummary {
    total: number
    invitable: number
    used: number
    remaining: number
}

export interface OrgMembersSummary {
    members: OrgMember[]
    seats: SeatSummary
}

/**
 * Fetch the list of org members + seat usage for the current (owner) user.
 */
export const fetchOrgMembers = async (): Promise<OrgMembersSummary | null> => {
    try {
        const { response } = await get<DefaultApiResponse<OrgMembersSummary>>({
            tag: fileName,
            url: '/account/members',
        })
        if (response.status === 'success' && response.data?.data) {
            return response.data.data
        }
        return null
    } catch (error) {
        logError('Error fetching org members', { error }, fileName)
        return null
    }
}

/**
 * Invite a user by email to the current org.
 */
export const inviteOrgMember = async (email: string): Promise<{ ok: boolean; message?: string }> => {
    try {
        const { response } = await post<DefaultApiResponse<{ inviteId: string }>>({
            tag: fileName,
            url: '/account/invite',
            body: { email },
        })
        if (response.status === 'success') return { ok: true }
        return { ok: false, message: response.message ?? 'Invite failed.' }
    } catch (error) {
        logError('Error inviting org member', { error }, fileName)
        return { ok: false, message: 'Unexpected error. Please try again.' }
    }
}

/**
 * Revoke an org member's access.
 */
export const revokeOrgMember = async (invitedUserId: string): Promise<boolean> => {
    try {
        const { response } = await remove<DefaultApiResponse<null>>({
            tag: fileName,
            url: `/account/members/${invitedUserId}`,
        })
        return response.status === 'success'
    } catch (error) {
        logError('Error revoking org member', { error }, fileName)
        return false
    }
}

/**
 * Fetch pending org invites for the current (invited) user.
 * Called on the AcceptInvitePage after registration.
 * ⚠️ Requires backend endpoint: GET /account/invite/pending
 */
export const fetchPendingInvites = async (): Promise<PendingInvite[]> => {
    try {
        const { response } = await get<DefaultApiResponse<{ invites: PendingInvite[] }>>({
            tag: fileName,
            url: '/account/invite/pending',
        })
        if (response.status === 'success' && response.data?.data?.invites) {
            return response.data.data.invites
        }
        return []
    } catch (error) {
        logError('Error fetching pending invites', { error }, fileName)
        return []
    }
}

/**
 * Accept a pending org invite.
 * ⚠️ Requires backend endpoint: POST /account/invite/accept
 */
export const acceptOrgInvite = async (inviteId: string): Promise<boolean> => {
    try {
        const { response } = await post<DefaultApiResponse<null>>({
            tag: fileName,
            url: '/account/invite/accept',
            body: { inviteId },
        })
        return response.status === 'success'
    } catch (error) {
        logError('Error accepting org invite', { error }, fileName)
        return false
    }
}

/**
 * Decline a pending org invite.
 * ⚠️ Requires backend endpoint: POST /account/invite/decline
 */
export const declineOrgInvite = async (inviteId: string): Promise<boolean> => {
    try {
        const { response } = await post<DefaultApiResponse<null>>({
            tag: fileName,
            url: '/account/invite/decline',
            body: { inviteId },
        })
        return response.status === 'success'
    } catch (error) {
        logError('Error declining org invite', { error }, fileName)
        return false
    }
}
